import { PropsWithChildren, lazy, Suspense } from 'react';
import { BrowserRouter } from 'react-router';
import { PreviousRoundResultsProvider } from '../contexts';
import { Query } from './Query.tsx';
import { Session } from './Session.tsx';
import { SessionB2B } from './SessionB2B.tsx';
import { Snackbar } from './Snackbar.tsx';
import { Styles } from './Styles.tsx';
import { Tooltip } from './Tooltip.tsx';
import { Wagmi } from './Wagmi.tsx';
import { BlockchainProviders } from './blockchain';
import { LoadingSpinner } from '../components/spinners';
import { b2bVersion } from '../utils/consts.ts';

// Lazy load RainbowKit only in B2C mode to avoid loading it in B2B
const B2CRainbowKitProvider = lazy(async () => {
  const { RainbowKitProvider } = await import('@rainbow-me/rainbowkit');
  return {
    default: RainbowKitProvider,
  };
});

const Root = ({ children }: PropsWithChildren) => {
  const content = (
    <PreviousRoundResultsProvider>
      <BrowserRouter>
        <Tooltip>{children}</Tooltip>
      </BrowserRouter>
    </PreviousRoundResultsProvider>
  );

  // Always render WagmiProvider (even in B2B) so wagmi hooks don't error
  // In B2B mode, it uses a stub config that won't connect to blockchain
  if (b2bVersion) {
    return (
      <Styles>
        <Snackbar>
          <Wagmi>
            <Query>
              <SessionB2B>{content}</SessionB2B>
            </Query>
          </Wagmi>
        </Snackbar>
      </Styles>
    );
  }

  // In B2C mode, use full wagmi/blockchain setup with RainbowKit
  return (
    <Styles>
      <Snackbar>
        <Wagmi>
          <Query>
            <Suspense
              fallback={
                <LoadingSpinner
                  style={{
                    margin: 'auto',
                  }}
                />
              }
            >
              <B2CRainbowKitProvider>
                <Session>
                  <BlockchainProviders>{content}</BlockchainProviders>
                </Session>
              </B2CRainbowKitProvider>
            </Suspense>
          </Query>
        </Wagmi>
      </Snackbar>
    </Styles>
  );
};

export { Root };
